import type { BookAttributesV1, SpreadLayoutId, TextBlock } from '@kitup/book-schema';
import { request } from './client.js';

export interface SignedPreviewAssetDto {
  readonly url: string;
  readonly urlExpiresAt: string;
}

export interface BookPreviewSceneDto {
  readonly id: string;
  readonly index: number;
  readonly layoutId: SpreadLayoutId | null;
  readonly textRu: string;
  readonly textBlocks: readonly TextBlock[];
  readonly bandImageRatio: number | null;
  readonly image: SignedPreviewAssetDto | null;
  readonly video: SignedPreviewAssetDto | null;
  readonly narration: SignedPreviewAssetDto | null;
}

export interface BookPreviewDto {
  readonly bookId: string;
  readonly title: string;
  /** The book's own language, not the interface's: `lang` on its text (screen readers). */
  readonly language: string;
  readonly subtitle: string | null;
  readonly dedication: string | null;
  readonly attributes: BookAttributesV1;
  readonly cover: SignedPreviewAssetDto | null;
  readonly backCover: SignedPreviewAssetDto | null;
  readonly scenes: readonly BookPreviewSceneDto[];
}

export type PublicBookSceneDto = Omit<BookPreviewSceneDto, 'id'>;

export interface PublicBookDto extends Omit<BookPreviewDto, 'bookId' | 'scenes'> {
  readonly scenes: readonly PublicBookSceneDto[];
}

export const bookPreviewApi = {
  get: (bookId: string) => request<BookPreviewDto>(`/books/${bookId}/preview`),
};

/** Чтение опубликованной книги по ссылке: работает и без входа. */
export const publicBookApi = {
  get: (token: string) => request<PublicBookDto>(`/public/books/${encodeURIComponent(token)}`),
};

/** Запас до истечения: перезапрашиваем заранее, а не в момент смерти ссылок. */
export const SIGNED_URL_REFRESH_MARGIN_MS = 60_000;

/** Не чаще раза в 15 секунд, даже если сервер отдал уже просроченные ссылки. */
const MIN_REFRESH_DELAY_MS = 15_000;

/**
 * Через сколько перезапросить превью, чтобы подписанные ссылки не протухли
 * посреди чтения.
 *
 * Media-ссылки на приватные объекты живут час (§3.3), а книгу читают вслух
 * с паузами — сессия легко переживает этот срок. Без перезапроса пользователь
 * возвращается к книге и видит пустые страницы: `retry` и `refetchOnWindowFocus`
 * в клиенте выключены, само оно не починится.
 *
 * Возвращает `null`, если подписывать нечего.
 */
export function millisUntilSignedUrlRefresh(
  preview: BookPreviewDto | PublicBookDto,
  now: number = Date.now(),
): number | null {
  const assets: readonly (SignedPreviewAssetDto | null)[] = [
    preview.cover,
    preview.backCover,
    ...preview.scenes.flatMap((scene) => [scene.image, scene.video, scene.narration]),
  ];

  const deadlines = assets
    // Проверка на истинность, а не на `null`: отсутствующее поле (старый ответ
    // апи, урезанная фикстура) роняло бы чтение книги на `urlExpiresAt`.
    .filter((asset): asset is SignedPreviewAssetDto => Boolean(asset))
    .map((asset) => Date.parse(asset.urlExpiresAt))
    .filter((value) => Number.isFinite(value));

  if (deadlines.length === 0) return null;

  return Math.max(
    MIN_REFRESH_DELAY_MS,
    Math.min(...deadlines) - now - SIGNED_URL_REFRESH_MARGIN_MS,
  );
}
